import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Divider,
  useTheme,
  Tooltip,
} from "@mui/material";
import { AccessTime, LocationOn, CheckCircle } from "@mui/icons-material";
import docImg from "../../assests/doctor.jpg";
import DoctorCardDetails from "./DoctorCard";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDoctorBySpeciality } from "../../Redux/Slice/DoctorSlice";
import SEO from "../../SEO";

const DoctorCard = ({ doctor, onBook }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={4}
      sx={{
        flex: "1 1 280px",
        maxWidth: 320,
        borderRadius: 3,
        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
        m: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Doctor Image + Basic Info */}
        <Box display="flex" alignItems="center" mb={2}>
          <CardMedia
            component="img"
            image={doctor.img || docImg}
            alt={doctor.name}
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              objectFit: "cover",
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
              mr: 2,
            }}
          />
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              {doctor.name}
              <Tooltip title="Verified Doctor" arrow>
                <CheckCircle
                  color="primary"
                  sx={{ fontSize: 18, verticalAlign: "middle" }}
                />
              </Tooltip>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25, fontWeight: 500 }}
            >
              {doctor.designation}
            </Typography>
          </Box>
        </Box>

        {/* Experience */}
        {doctor.experience && (
          <Box
            display="flex"
            alignItems="center"
            color={theme.palette.text.secondary}
            mb={1.5}
            gap={1}
          >
            <AccessTime fontSize="small" />
            <Typography variant="body2">
              {doctor.experience} Years Experience
            </Typography>
          </Box>
        )}

        {/* Hospital and Location */}
        <Box
          display="flex"
          alignItems="center"
          color={theme.palette.text.secondary}
          mb={2}
          gap={1}
        >
          <LocationOn fontSize="small" />
          <Typography variant="body2">
            {doctor.hospitalName}, {doctor.location}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Speciality */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: "italic", lineHeight: 1.4 }}
        >
          Speciality: {doctor.speciality}
        </Typography>
      </CardContent>

      {/* Button */}
      <Box sx={{ p: 2 }}>
        <Button
          variant="contained"
          size="medium"
          fullWidth
          onClick={onBook}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Book Appointment
        </Button>
      </Box>
    </Card>
  );
};

const SpecialityDetails = () => {
  const dispatch = useDispatch();
  const [showDoc, setShowDoc] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedSpecialityId, setSelectedSpecialityId] = useState(null);
  const { specialityName } = useParams();
  const { specialityItems } = useSelector((state) => state.doctor);

  console.log("specialityItems", specialityItems.data);

  useEffect(() => {
    if (specialityName) {
      dispatch(getDoctorBySpeciality({ specialityName }));
    }
  }, [dispatch, specialityName]);

  return (
    <>
      <SEO
        title={`${specialityName} – Doctors & Specialists | Trust Doc`}
        description={`Find the best ${specialityName} specialists and book appointments online.`}
        canonical={`https://www.trust-doc.com/speciality/${specialityName}`}
      />
      <Box>
        {/* Header Bar */}
        <Box
          sx={{
            height: 80,
            width: "100%",
            background: "linear-gradient(to right, #0e102d, #a0cde8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            pl: 10,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "white", fontWeight: 600 }}
          >
            {!showDoc ? "Specializations" : "Book Appointment"}
          </Typography>
        </Box>

        {/* Doctor List or Details */}
        {!showDoc ? (
          <Box sx={{ p: "2rem 5rem" }}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ mb: 2, fontSize: "1.25rem" }}
            >
              Doctors near you
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                m: "-8px",
              }}
            >
              {specialityItems?.data?.map((doc) => (
                <DoctorCard
                  key={doc.doctorId}
                  doctor={{
                    name: doc.doctorName,
                    designation: doc.designation,
                    experience: doc.experience,
                    speciality: doc.speciality?.specialityName,
                    hospitalName: doc.hospital?.hospitalName,
                    location: doc.hospital?.address || "",
                    img: doc.doctorImage || docImg,
                  }}
                  onBook={() => {
                    setSelectedDoctorId(doc.doctorId);
                    setSelectedSpecialityId(doc.speciality?.specialityId);
                    setShowDoc(true);
                  }}
                />
              ))}

              {/* No Doctors Found */}
              {specialityItems?.data?.length === 0 && (
                <Typography variant="body2">
                  No doctors found for this specialization.
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: "2rem 5rem" }}>
            <DoctorCardDetails
              doctorId={selectedDoctorId}
              specialityId={selectedSpecialityId}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default SpecialityDetails;
